import * as Sentry from '@sentry/react'
import _get from 'lodash/get'

import { profileSelector } from '@/selectors'

import config from '../config'

export const initSentry = () => {
    Sentry.init({
        dsn: config.isProduction || config.isStaging ? config.sentryDsn : null,
        normalizeDepth: 5,
        environment: config.environment,
        beforeBreadcrumb(breadcrumb, hint) {
            let message = breadcrumb.message
            if (breadcrumb.category === 'ui.click') {
                const evt = hint?.event as Event | undefined
                const target = evt?.target ?? null
                if (target instanceof Node) {
                    const text = target.textContent ?? ''
                    if (text.length > 0) {
                        message = `${message} ("${text.substring(0, 20)}${text.length > 20 ? '...' : ''}")`
                    }
                }
            }
            return { ...breadcrumb, message }
        },
        sendDefaultPii: true,
        integrations: [
            Sentry.browserTracingIntegration(),
            // unmasking specific elements to help identify issues
            Sentry.replayIntegration({
                unmask: ['[data-sentry-unmask]'],
            }),
        ],
        // Tracing
        tracesSampleRate: 0.1, //  Capture 10% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/staging\.taxfix\.tech\/es-es\/app/,
            /^https:\/\/taxfix\.com\/es-es\/app/,
        ],
        // Session Replay
        // Sessions without errors will not be recorded
        replaysSessionSampleRate: 0,
        // Production sessions with errors will be recorded 10% of the time
        replaysOnErrorSampleRate: config.isProduction ? 0.1 : 0,
        // Enable logs to be sent to Sentry
        enableLogs: true,
        release: import.meta.env.REACT_APP_RELEASE_VERSION,
    })
}

export const initSentryUser = (profile: ReturnType<typeof profileSelector>) => {
    Sentry.getCurrentScope().setUser({
        id: profile.id,
        variant: profile.variant,
    })
}

// Helpers
const safeGet = <T>(obj: unknown, path: (string | number)[], fallback: T): T => {
    // lodash/get expects object; casting unknown to object is safe here for retrieval only
    return _get(obj as object, path, fallback) as T
}

const toContextValue = (value: unknown): Sentry.Context => {
    if (value && typeof value === 'object') return value as Sentry.Context
    return { value: String(value) }
}

export const sendApiErrorsToSentry = (exception: unknown) => {
    Sentry.withScope((scope) => {
        scope.setContext('apiCall', {
            requestData: JSON.stringify(safeGet(exception, ['response', 'config', 'data'], {} as unknown)),
            responseData: JSON.stringify(safeGet(exception, ['response', 'data'], {} as unknown)),
            sentryXhr: JSON.stringify(safeGet(exception, ['response', 'request'], {} as unknown)),
        })

        // Try to parse more info from Axios. If fails, report as exception.
        try {
            const apiStatus = safeGet<string>(exception, ['response', 'status'], '')
            const apiMethod = safeGet<string>(exception, ['response', 'config', 'method'], '')
            const apiEndpoint = formatApiEndpoint(safeGet<string>(exception, ['request', 'responseURL'], ''))

            Sentry.captureMessage(`${apiMethod.toUpperCase()} ${apiEndpoint} [${apiStatus}]`, {
                level: 'info', // https://github.com/getsentry/sentry-javascript/blob/7.0.0/MIGRATION.md#severity-severitylevel-and-severitylevels
                fingerprint: ['{{ default }}', String(apiStatus), String(apiMethod), String(apiEndpoint)],
            })
        } catch {
            Sentry.captureException(exception)
        }
    })
}

export const sendDecodingErrorsToSentry = (exception: unknown, string: string, componentName = 'a component') => {
    Sentry.captureMessage(`Decoding failed in ${componentName}`, {
        contexts: {
            decoding: { value: string, componentName, exception: String(exception) },
        },
        fingerprint: ['{{ default }}', 'DecodingError', componentName],
    })
}

export const reportFileUploadError = (exception: unknown, file: File | null | undefined) => {
    const fileName = file?.name ?? 'unknown'
    const fileExtension = fileName.split('.').slice(-1)[0]
    const errorMessage = safeGet<string>(exception, ['response', 'data', 'message'], '')

    if (safeGet<number | string>(exception, ['response', 'status'], 0) === 401) return

    Sentry.captureMessage(`File upload failed`, {
        contexts: {
            error: { message: errorMessage, fileExtension },
        },
        tags: {
            'fileUploadError.fileExtension': fileExtension === fileName ? 'N/A' : fileExtension,
            'fileUploadError.errorMessage': errorMessage,
        },
        fingerprint: ['{{ default }}', 'FileUploadError'],
    })
}

export const reportNylasMissingConfigurations = (exception: unknown, data?: unknown) => {
    Sentry.captureMessage(`Missing Nylas configurations to open calendar`, {
        contexts: { exception: toContextValue(exception), data: toContextValue(data) },
        fingerprint: ['{{ default }}', 'NylasMissingConfigurations'],
    })
}

export const reportNylasSchedulerError = (exception: unknown, data: unknown) => {
    Sentry.captureMessage(`Nylas scheduler error`, {
        contexts: { exception: toContextValue(exception), data: toContextValue(data) },
        fingerprint: ['{{ default }}', 'NylasSchedulerError'],
    })
}

const regexDigits = /\d+/g
const regexUUID = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}$/i
const cleanPathnames = [
    '/api/payment/paymentconfirmation',
    '/api/magiclink',
    '/api/subscriptions/subscriptionconfirmation',
]
const formatApiEndpoint = (urlString: string) => {
    if (!urlString) return urlString

    const { pathname: apiEndpoint } = new URL(urlString)

    const newPathnameIndex = cleanPathnames.findIndex((cleanerPathname) => apiEndpoint.includes(cleanerPathname))
    if (newPathnameIndex >= 0) return cleanPathnames[newPathnameIndex]

    return apiEndpoint
        .split('/')
        .map((pathnamePart) => pathnamePart.replace(regexUUID, '{uuid}').replace(regexDigits, '{id}'))
        .join('/')
}
